import { useSearchParams } from "react-router-dom";

/**
 * Sync a tab/view selection with a URL search param so that back navigation
 * returns the user to the tab they were on.
 *
 * Pairs with DealLink (which preserves location.pathname + location.search as
 * state.from) and DealDetail's back button (which navigates to state.from).
 *
 * Usage:
 *   const [tab, setTab] = useTabParam("tab", "today", ["today","unscheduled","pm"] as const);
 *
 * Behavior:
 *  - Reads the param from the current URL on every render (truthy + in validTabs => use it).
 *  - Falls back to defaultTab when the param is missing or invalid for this view.
 *  - setTab writes via { replace: true } so flipping tabs doesn't pollute browser history.
 *  - When the new value equals the default, the param is removed from the URL (clean).
 */
export function useTabParam<T extends string>(
  paramName: string,
  defaultTab: T,
  validTabs?: readonly T[],
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(paramName) as T | null;
  const isValid = raw != null && (!validTabs || validTabs.includes(raw));
  const tab: T = isValid ? (raw as T) : defaultTab;

  const setTab = (next: T) => {
    // IMPORTANT: read live URL via window.location.search — NOT the closure
    // `params` snapshot and NOT setParams's function form. react-router's
    // useSearchParams setter (even with the prev-fn form) passes a snapshot
    // captured at render time, so back-to-back calls in the same handler
    // (e.g. DispatchBoard.handleViewChange firing setViewAs() then setTab())
    // both see the same stale base and the second call clobbers the first.
    // history.replaceState updates window.location.search synchronously, so
    // reading it here always reflects the previous call's update.
    const current = new URLSearchParams(window.location.search);
    if (next === defaultTab) {
      current.delete(paramName);
    } else {
      current.set(paramName, next);
    }
    setParams(current, { replace: true });
  };

  return [tab, setTab];
}
